<template>
  <Layout>
    <McAlert
      class="template-readonly__sticky-alert mb-3"
      variant="info"
      withIcon
      :text="$t('vehicleInspection.templateReadonly.banner')"
    />

    <PageHeader :title="$t('vehicleInspection.templateReadonly.title')">
      <template #afterTitle>
        <McButtonBack :data-me="`${dataMePrefix}-btn-back`" />
      </template>
    </PageHeader>

    <b-overlay :show="isLoading" class="template-readonly__overlay" rounded="sm" opacity="0.6">
    <div v-if="inspection && template">
    <section class="template-readonly__section">
      <h4>{{ $t('vehicleInspection.templateReadonly.inspectionReference') }}</h4>
      <b-card>
        <ReadOnly :items="referenceItems" />
      </b-card>
    </section>

    <section class="template-readonly__section">
      <h4>{{ $t('vehicleInspection.templateReadonly.templateInformation') }}</h4>
      <b-card class="template-readonly__information">
        <ReadOnly :items="templateItems" />
      </b-card>
    </section>

    <section class="template-readonly__section">
      <h4>{{ $t('vehicleInspection.templateReadonly.inspectionList') }}</h4>
      <div class="template-readonly__categories">
        <b-card
          v-for="(section, sectionIndex) in sections"
          :key="section.sectionId"
          no-body
          :class="[
            'template-readonly__category',
            { 'template-readonly__category--active': section.isExpanded },
          ]"
        >
          <button
            :data-me="`${dataMePrefix}-btn-toggle-section-${section.sectionId}`"
            type="button"
            class="template-readonly__category-header"
            :aria-expanded="section.isExpanded"
            :aria-controls="`readonly-template-section-${section.sectionId}`"
            @click="toggleSection(sectionIndex)"
          >
            <span class="template-readonly__category-name">{{ section.name }}</span>
            <span class="template-readonly__category-actions">
              <McBadge
                :text="questionCountLabel(section.questions.length)"
                variant="outlineSecondary"
                size="medium"
                type="button"
              />
              <i
                :class="[
                  'mc-icon',
                  section.isExpanded
                    ? 'icon-outline_chevron-up'
                    : 'icon-outline_chevron-down',
                ]"
              />
            </span>
          </button>

          <div
            v-show="section.isExpanded"
            :id="`readonly-template-section-${section.sectionId}`"
            class="template-readonly__category-body"
          >
            <h5>{{ $t('vehicleInspection.detail.question') }}</h5>

            <article
              v-for="(question, questionIndex) in section.questions"
              :key="question.questionId"
              class="template-readonly__question"
            >
              <div class="template-readonly__question-header">
                <div class="template-readonly__question-title">
                  <span>{{ questionIndex + 1 }}.</span>
                  <span>
                    {{ question.question }}<span v-if="question.isRequired" class="text-danger">*</span>
                  </span>
                </div>

                <div class="template-readonly__answer-format">
                  <template v-if="isRadioQuestion(question)">
                    <McBadge
                      :text="question.unhealthyLabel || emptyValue"
                      variant="outlineDanger"
                      size="medium"
                      type="button"
                    />
                    <span>/</span>
                    <McBadge
                      :text="question.healthyLabel || emptyValue"
                      variant="outlineSuccess"
                      size="medium"
                      type="button"
                    />
                  </template>
                  <McBadge
                    v-else
                    :text="$t('vehicleInspection.templateReadonly.text')"
                    variant="outlineSecondary"
                    size="medium"
                    type="button"
                  />
                </div>
              </div>

              <div class="template-readonly__evidence">
                <h6>{{ $t('vehicleInspection.detail.supportingEvidence') }}</h6>
                <div class="template-readonly__policies">
                  <span v-if="question.notePolicy">
                    {{ $t('vehicleInspection.detail.note') }}:
                    {{ policyLabel(question.notePolicy) }}
                  </span>
                  <span>
                    {{ $t('vehicleInspection.templateReadonly.photo') }}:
                    {{ policyLabel(question.photoPolicy) }}
                  </span>
                </div>
              </div>
            </article>
          </div>
        </b-card>
      </div>
    </section>
    </div>
    <McEmptyState
      v-else-if="!isLoading"
      :title="$t('vehicleInspection.templateEmptyState.title')"
      :hasButton="false"
      size="sm"
    />
    </b-overlay>
  </Layout>
</template>

<script>
import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import McAlert from '@/components/McAlert'
import McButtonBack from '@/components/McButtonBack'
import McBadge from '@/components/McBadge'
import McEmptyState from '@/components/McEmptyState'
import ReadOnly from '@/router/views/vehicle-inspection/components/ReadOnly.vue'
import { GET_VEHICLE_INSPECTION } from '@/services/api/vehicleInspection'
import { GET_VEHICLE_INSPECTION_TEMPLATE } from '@/services/api/setting/vehicleInspection'
import {
  buildVehicleInspectionInput,
  normalizeVehicleInspectionResponse,
} from '@/router/views/vehicle-inspection/lib/vehicleInspectionAdapter'

export default {
  page: {
    title: 'Detail Template Pemeriksaan',
  },
  components: {
    Layout,
    PageHeader,
    McAlert,
    McButtonBack,
    McBadge,
    McEmptyState,
    ReadOnly,
  },
  data() {
    return {
      dataMePrefix: 'vehicle-inspection-template-readonly',
      inspection: null,
      template: null,
      sections: [],
      isLoading: false,
    }
  },
  computed: {
    emptyValue() {
      return '-'
    },
    referenceItems() {
      return [
        {
          key: 'inspectionNumber',
          label: this.$t('vehicleInspection.templateReadonly.inspectionNumber'),
          value: this.inspection.documentNumber,
        },
        {
          key: 'vehicle',
          label: this.$t('vehicleInspection.field.vehicle'),
          value: this.inspection.submittedLicensePlate,
        },
        {
          key: 'inspector',
          label: this.$t('vehicleInspection.field.inspectedBy'),
          value: this.inspection.inspectorName,
        },
        {
          key: 'inspectedAt',
          label: this.$t('vehicleInspection.templateReadonly.inspectedAt'),
          value: this.formatInspectionDate(this.inspection.submittedAt),
        },
      ]
    },
    templateItems() {
      return [
        {
          key: 'templateName',
          label: this.$t('vehicleInspection.field.templateName'),
          value: this.template.name || this.emptyValue,
        },
        {
          key: 'description',
          label: this.$t('vehicleInspection.templateReadonly.description'),
          value: this.template.description || this.emptyValue,
        },
        {
          key: 'vehicleTarget',
          label: this.$t('vehicleInspection.templateReadonly.vehicleTarget'),
          value: this.vehicleTarget,
        },
      ]
    },
    vehicleTarget() {
      if (this.template.allVehicles) return this.$t('vehicleInspection.fields.allVehicles')

      const vehicles = (this.template.vehicles ?? []).map((vehicle) => vehicle.licensePlate)
      if (vehicles.length) return vehicles.join(', ')

      const vehicleGroups = (this.template.vgroups ?? []).map((group) => group.name)
      return vehicleGroups.join(', ') || this.emptyValue
    },
  },
  watch: {
    '$route.fullPath': {
      immediate: true,
      handler() {
        this.getReadonlyTemplate()
      },
    },
  },
  methods: {
    async getReadonlyTemplate() {
      const inspectionId = Number(this.$route.query.inspectionId)
      const templateId = Number(this.$route.query.templateId)
      const versionNumber = Number(this.$route.query.versionNumber)

      this.inspection = null
      this.template = null
      this.sections = []
      this.isLoading = false

      if (
        !Number.isInteger(inspectionId) ||
        inspectionId <= 0 ||
        !Number.isInteger(templateId) ||
        templateId <= 0 ||
        !Number.isInteger(versionNumber) ||
        versionNumber <= 0
      ) {
        return
      }

      this.isLoading = true
      try {
        const [inspectionResponse, templateResponse] = await Promise.all([
          this.$apollo.query({
            query: GET_VEHICLE_INSPECTION,
            fetchPolicy: 'no-cache',
            variables: { filter: buildVehicleInspectionInput(inspectionId) },
          }),
          this.$apollo.query({
            query: GET_VEHICLE_INSPECTION_TEMPLATE,
            fetchPolicy: 'no-cache',
            variables: { filter: { templateId, versionNumber } },
          }),
        ])
        const inspectionResult = inspectionResponse.data.vehicleInspection
        const templateResult = templateResponse.data.vehicleInspectionTemplate

        if (!inspectionResult?.isSuccessful) throw new Error(inspectionResult?.message)
        if (!templateResult?.isSuccessful) throw new Error(templateResult?.message)

        this.inspection = normalizeVehicleInspectionResponse(inspectionResponse.data)
        this.template = templateResult.vehicleInspectionTemplate
        this.sections = (this.template?.sections ?? [])
          .map((section, index) => ({
            ...section,
            isExpanded: index === 0,
            questions: [...(section.questions ?? [])].sort(
              (firstQuestion, secondQuestion) =>
                firstQuestion.sortOrder - secondQuestion.sortOrder,
            ),
          }))
          .sort((firstSection, secondSection) => firstSection.sortOrder - secondSection.sortOrder)
      } catch (error) {
        this.$mcToast.error(error.message || this.$t('global.somethingWentWrong'))
      } finally {
        this.isLoading = false
      }
    },
    formatInspectionDate(value) {
      if (!value) return this.emptyValue

      const locale = this.$i18n.locale === 'en' ? 'en-US' : 'id-ID'
      const date = new Date(value)
      const formattedDate = new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(date)
      const formattedTime = new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(date)

      return `${formattedDate}, ${formattedTime} WIB`
    },
    toggleSection(sectionIndex) {
      this.sections = this.sections.map((section, index) => ({
        ...section,
        isExpanded: index === sectionIndex ? !section.isExpanded : false,
      }))
    },
    questionCountLabel(count) {
      return this.$t('vehicleInspection.detail.questionCount', { count })
    },
    isRadioQuestion(question) {
      return String(question.type).replace(/[^a-z]/gi, '').toLowerCase() === 'radiobutton'
    },
    policyLabel(policy) {
      const translationKeys = {
        optional: 'optional',
        required: 'required',
        requiredonunhealthy: 'requiredOnUnhealthy',
      }
      const normalizedPolicy = String(policy).replace(/[^a-z]/gi, '').toLowerCase()
      const translationKey = translationKeys[normalizedPolicy]
      return translationKey
        ? this.$t(`vehicleInspection.templateReadonly.${translationKey}`)
        : '-'
    },
  },
}
</script>

<style lang="scss" scoped>
.template-readonly {
  &__sticky-alert {
    position: sticky;
    top: calc($header-height + 10px);
    z-index: $zindex-sticky;
  }

  &__overlay {
    min-height: 160px;
  }

  &__section {
    margin-bottom: 20px;

    h4 {
      font-size: 16px;
      font-weight: $font-weight-bold;
      margin-bottom: 12px;
    }

    ::v-deep .card-body {
      padding: 16px;
    }
  }

  &__information {
    ::v-deep .mc-readonly-list__item:nth-child(2) {
      flex: 2 1 480px;
    }
  }

  &__categories {
    display: grid;
    gap: 14px;
  }

  &__category {
    overflow: hidden;

    &--active {
      border: 2px solid #a1c5f7;
    }
  }

  &__category-header {
    align-items: center;
    background: $white;
    border: 0;
    color: inherit;
    display: flex;
    gap: 12px;
    padding: 12px;
    text-align: left;
    width: 100%;
  }

  &__category-name {
    flex: 1;
    font-size: 14px;
    font-weight: $font-weight-bold;
  }

  &__category-actions,
  &__answer-format {
    align-items: center;
    display: flex;
    gap: 6px;
  }

  &__category-body {
    border-top: 1px solid #e6ebf2;
    padding: 14px 12px 0;

    > h5 {
      font-size: 14px;
      font-weight: bold;
      margin-bottom: 8px;
    }
  }

  &__question {
    border-bottom: 1px solid #e6ebf2;
    padding: 12px 14px;

    &:last-child {
      border-bottom: 0;
    }
  }

  &__question-header,
  &__question-title {
    align-items: flex-start;
    display: flex;
    gap: 8px;
  }

  &__question-header {
    justify-content: space-between;
  }

  &__question-title {
    flex: 1;
  }

  &__evidence {
    margin: 10px 0 0 24px;

    h6 {
      font-size: 14px;
      font-weight: bold;
      margin-bottom: 6px;
    }
  }

  &__policies {
    color: $mep-color-text-400;
    display: flex;
    flex-wrap: wrap;
    font-size: 13px;
    gap: 8px 20px;
  }

  @media (max-width: 767px) {
    &__question-header {
      flex-direction: column;
    }

    &__answer-format {
      margin-left: 24px;
    }
  }
}
</style>
